import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { createPortal } from 'react-dom';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { extractTemuList, pickField } from '@/temu-platform/utils/temuResponse';
import { isBindableAgency } from '@/temu-platform/modules/compliance/complianceDisplay';
import { showToast } from '@/utils/toast';
import '@/styles/temu-platform/temu-product-ops.css';

/**
 * 合规信息编辑：对齐 GLOBAL 流程
 * metadata.get → 展示 complianceInfoList → compliance.edit(editReqList)
 */
export default function ComplianceInfoEditModal({
  open = false,
  shopId,
  productId,
  productTitle = '',
  onClose,
  onSaved,
}) {
  const { t } = useTranslation('workbench');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [infoList, setInfoList] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [repByType, setRepByType] = useState({});

  const pid = Number(productId) || 0;

  useEffect(() => {
    if (!open || !shopId || pid <= 0) {
      setInfoList([]);
      setAgencies([]);
      setRepByType({});
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const [metaRes, agencyRes] = await Promise.all([
          temuPlatformAPI.listComplianceRequirements(shopId, { productId: pid }),
          temuPlatformAPI.listComplianceAgencies(shopId, { page: 0, pageSize: 100 }).catch(() => null),
        ]);
        if (cancelled) return;
        const list = Array.isArray(metaRes?.result?.complianceInfoList)
          ? metaRes.result.complianceInfoList
          : (Array.isArray(metaRes?.complianceInfoList) ? metaRes.complianceInfoList : []);
        setInfoList(list);
        const agencyRows = extractTemuList(agencyRes) || [];
        setAgencies(agencyRows.filter(isBindableAgency));
        const next = {};
        list.forEach((item) => {
          const type = Number(item?.complianceType) || 0;
          const reps = Array.isArray(item?.repDetailList) ? item.repDetailList : [];
          const first = reps.find((r) => Number(r?.repId) > 0);
          if (type > 0 && first) next[type] = String(first.repId);
        });
        setRepByType(next);
      } catch (e) {
        if (!cancelled) {
          setInfoList([]);
          showToast(e?.message || t('compliance.infoMetaFail'), 'error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, shopId, pid, t]);

  const agencyOptions = useMemo(
    () => agencies.map((row) => {
      const id = pickField(row, 'agencyId', 'repId');
      const name = pickField(row, 'agencyName', 'repName') || id;
      return { value: String(id), label: String(name) };
    }).filter((o) => o.value && o.value !== 'undefined'),
    [agencies],
  );

  if (!open || typeof document === 'undefined') return null;

  const onSave = async () => {
    if (!shopId || pid <= 0) return;
    const editReqList = infoList
      .map((item) => {
        const type = Number(item?.complianceType) || 0;
        if (type <= 0) return null;
        const repId = Number(repByType[type] || 0);
        if (!repId) return null;
        return {
          complianceType: type,
          repList: [{ repId }],
        };
      })
      .filter(Boolean);
    if (!editReqList.length) {
      showToast(t('compliance.infoPickRep'), 'warning');
      return;
    }
    setSaving(true);
    try {
      const res = await temuPlatformAPI.editComplianceInfo(shopId, {
        productId: pid,
        editReqList,
      });
      const ok = res?.success === true
        || Number(res?.errorCode) === 1000000
        || Number(res?.errorCode) === 0;
      if (!ok && res?.errorMsg) {
        throw new Error(res.errorMsg);
      }
      showToast(t('compliance.infoSubmitted'), 'success');
      onSaved?.();
      onClose?.();
    } catch (e) {
      showToast(e?.message || t('compliance.infoEditFail'), 'error');
    } finally {
      setSaving(false);
    }
  };

  return createPortal(
    <div className="tpo-batch-modal tpo-cert-modal" role="presentation">
      <button
        type="button"
        className="tpo-batch-modal__backdrop"
        aria-label={t('moduleShell.close')}
        onClick={onClose}
      />
      <div
        className="tpo-batch-modal__panel tpo-cert-modal__panel tpo-compliance-info-edit"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-compliance-info-edit-title"
      >
        <div className="tpo-batch-modal__head">
          <h2 id="tpo-compliance-info-edit-title">
            {t('compliance.infoEditTitle')}{productTitle ? ` · ${productTitle}` : ''}
          </h2>
          <button type="button" className="tpo-batch-modal__close" onClick={onClose} aria-label={t('moduleShell.close')}>×</button>
        </div>
        <div className="tpo-batch-modal__body tpo-cert-modal__body">
          <p className="tpo-batch-modal__hint">
            {t('compliance.infoEditHint', { id: pid || '—' })}
          </p>
          {loading ? (
            <p>{t('compliance.infoLoadingMeta')}</p>
          ) : infoList.length === 0 ? (
            <p className="tpo-muted">{t('compliance.infoNoMeta')}</p>
          ) : (
            <ul className="tpo-compliance-info-edit__list">
              {infoList.map((item, idx) => {
                const type = Number(item?.complianceType) || 0;
                const name = String(item?.name || item?.desc || i18n.t('compliance.infoItem', { ns: 'workbench', n: type || idx + 1 })).trim();
                const status = item?.status;
                const statusOk = status != null && Number(status) > 0;
                return (
                  <li key={`${type}-${idx}`} className="tpo-compliance-info-edit__row">
                    <div className="tpo-compliance-info-edit__meta">
                      <span className={`tpo-compliance-info-list__dot tpo-compliance-info-list__dot--${statusOk ? 'ok' : 'bad'}`} />
                      <strong>{name}</strong>
                      <span className="tpo-muted">type={type || '—'}</span>
                    </div>
                    <select
                      className="tpo-select"
                      value={repByType[type] || ''}
                      disabled={saving || agencyOptions.length === 0}
                      onChange={(e) => {
                        const v = e.target.value;
                        setRepByType((prev) => ({ ...prev, [type]: v }));
                      }}
                      aria-label={t('compliance.infoRepAria', { name })}
                    >
                      <option value="">{t('compliance.infoPickRepPh')}</option>
                      {agencyOptions.map((opt) => (
                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                      ))}
                    </select>
                  </li>
                );
              })}
            </ul>
          )}
          {agencyOptions.length === 0 && !loading ? (
            <p className="tpo-muted">
              {t('compliance.infoNoAgency')}
            </p>
          ) : null}
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--secondary" onClick={onClose} disabled={saving}>
            {t('shop.cancel')}
          </button>
          <button
            type="button"
            className="tpo-btn tpo-btn--primary"
            onClick={onSave}
            disabled={saving || loading || infoList.length === 0}
          >
            {saving ? t('returnList.submitting') : t('compliance.infoSubmitEdit')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
